import {
  FieldTypes,
  IFieldInstance,
} from "../../types/fieldInstance.interface";
import {
  transformedData,
  transformRLData,
} from "../formGenUtils/getTransformedData";
import { isValueSelected } from "../formGenUtils/viewModelMethods";
import relatedListFormFields from "../formGenUtils/formRLs.json";
import moment from "moment";

const { unnamed: unnamedFields, ...namedFields } = transformedData;

// find these names by checking the form builder for field types that match the RLs : medRL etc.

const validFields = Object.values(namedFields).flat() as IFieldInstance[];

export const incidentRelatedLists = validFields.reduce((acc: any, field) => {
  if (
    field.fieldType === FieldTypes.RelatedList ||
    field.fieldType === FieldTypes.InlineRelatedList
  ) {
    const rlType = field.dataType.match(/RL\((.*?)\)/);
    if (rlType) {
      acc[rlType[1]] = field.fieldName;
    }
  }
  return acc;
}, {});

export const inlineRelatedListFields = validFields.reduce((acc: any, field) => {
  if (field.fieldType === FieldTypes.InlineRelatedList) {
    const rlType = field.dataType.match(/RL\((.*?)\)/);
    if (rlType) {
      acc[rlType[1]] = field.fieldName;
    }
  }
  return acc;
}, {});

const inlineRLFields = Object.keys(relatedListFormFields).reduce(
  (acc: any, key) => {
    if (Object.keys(inlineRelatedListFields).includes(key)) {
      acc[key] =
        relatedListFormFields[key as keyof typeof relatedListFormFields];
    }
    return acc;
  },
  {}
);

export const transformedInlineRLFields = transformRLData(inlineRLFields);

function getObjectKey(obj: any, value: string) {
  return Object.keys(obj).find((key) => obj[key] === value);
}

const transformedRLFormFields = transformRLData(relatedListFormFields);

export const getRLType = (fieldName: string) => {
  return getObjectKey(incidentRelatedLists, fieldName);
};

export function extractNumber(str: string): number | null {
  const match = str.match(/\[(\d+)\]/);
  if (match) {
    return parseInt(match[1], 10);
  }
  return null;
}

export const getRLFormFields = (fieldName: string) => {
  const key = getObjectKey(incidentRelatedLists, fieldName);
  return key
    ? transformedRLFormFields[key as keyof typeof transformedRLFormFields]
    : null;
};

export const getRLValues = (values: any, fieldNames: string[]) => {
  const rlValues: any = {};
  const inlineRLFields = Object.keys(transformedInlineRLFields).map(
    (field) => incidentRelatedLists[field]
  );
  const _fieldNames = fieldNames.filter(
    (fieldName) => !inlineRLFields.includes(fieldName)
  );
  _fieldNames.forEach((fieldName) => {
    const modifiedValues = (values[fieldName] || []).map(
      (value: any, index: number) => {
        return {
          ...value,
          index,
        };
      }
    );
    rlValues[fieldName] = modifiedValues;
  });
  return rlValues;
};

// A helper function to find fields with the same parent
const findChildFields = (fields: IFieldInstance[], parentId: string) => {
  return fields.filter((field) => field.parentFieldId === parentId);
};

export function getSelectedValueLabel(val: string, options: any[]) {
  return (
    options.find((option: { value: any }) => option.value === val)?.label ?? ""
  );
}

const toggleBoxLogic = (
  field: IFieldInstance,
  fields: IFieldInstance[],
  row: any
): { [x: string]: boolean } | undefined => {
  let toggleBox;
  let result: { [x: string]: boolean } = {};
  if (field.childFieldIds.length > 0) {
    toggleBox = fields.find(
      (_field) =>
        field.childFieldIds.includes(_field.id) &&
        _field.fieldType === FieldTypes.Togglebox
    );
  }
  if (toggleBox) {
    result[toggleBox.fieldName] = row[toggleBox.fieldName];
    return result;
  }
  return undefined;
};

const modifyValueForRender = (
  row: any,
  field: IFieldInstance,
  fields: IFieldInstance[],
  allLookUpOptions: any[]
) => {
  let value = row[field.fieldName];
  if (field.fieldType === FieldTypes.Lookup) {
    value = getSelectedValueLabel(row[field.fieldName], allLookUpOptions);
  }
  if (field.fieldType === FieldTypes.DateTime) {
    value = row[field.fieldName]
      ? moment(row[field.fieldName]).format("MM/DD/YYYY, hh:mm a")
      : "-";
  }
  if (field.fieldType === FieldTypes.CustomWoundComp) {
    const allWoundFields = fields.filter(
      (field) => field.fieldType === FieldTypes.CustomWoundComp
    );
    value = allWoundFields
      .map((field) => `${field.fieldLabel} : ${row?.[field.fieldName] ?? "-"}`)
      .join(", ");
  }
  return value;
};

const getIndependentFieldAccessor = (
  field: IFieldInstance,
  fields: IFieldInstance[],
  toggleBoxLogic: { [x: string]: boolean } | undefined,
  allLookUpOptions: any,
  row: { [x: string]: any }
) => {
  let toggleChild =
    toggleBoxLogic &&
    fields.find(
      (_field) => _field.fieldName === Object.keys(toggleBoxLogic)[0]
    );
  if (toggleBoxLogic && Object.values(toggleBoxLogic)[0]) {
    const toggleControlledField = fields.find((_field) =>
      toggleChild?.childFieldIds.includes(_field.id)
    );
    return (
      toggleChild &&
      toggleControlledField &&
      row[toggleControlledField?.fieldName]
    );
  } else {
    return modifyValueForRender(row, field, fields, allLookUpOptions);
  }
};

const createAccessor = (
  fields: IFieldInstance[],
  parentIds: string[],
  allLookUpOptions: any[]
) => {
  return (row: { [x: string]: any }) => {
    const parentFields = fields.filter((field) => parentIds.includes(field.id));
    if (parentFields.length) {
      const dependentFields = parentFields
        .map((parentfield) => findChildFields(fields, parentfield.id))
        .flat();
      const parentColumn = row[parentFields[0].fieldName]; // will not always work
      const selectedField = dependentFields.find((field) => {
        return isValueSelected([field.condition].flat(), [parentColumn].flat());
      });
      if (selectedField) {
        const toggleBoxDetails = toggleBoxLogic(selectedField, fields, row);
        return getIndependentFieldAccessor(
          selectedField,
          fields,
          toggleBoxDetails,
          allLookUpOptions,
          row
        );
      }
    }
    return undefined;
  };
};

export const getDependentFieldColumns = (
  allLookUpOptions: any,
  fields: IFieldInstance[],
  field: IFieldInstance,
  parentIds?: string[]
) => {
  // Use either the shared substring or the fieldLabel as Header
  const header = field.tableLabel || field.fieldLabel;
  // change
  // Use a function for accessor that returns the right fieldName based on parent value
  return {
    Header: header,
    accessor: createAccessor(
      fields,
      parentIds || [field.parentFieldId],
      allLookUpOptions
    ),
  };
};

const extendedChildFields = (
  childFields: IFieldInstance[],
  fields: IFieldInstance[]
) => {
  const toggleControlledFields: IFieldInstance[] = [];
  childFields.forEach((childField) => {
    const toggleBox = fields.find(
      (_field) =>
        childField.childFieldIds.includes(_field.id) &&
        _field.fieldType === FieldTypes.Togglebox
    );
    if (toggleBox) {
      const toggleControlledField = fields.find((_field) =>
        toggleBox.childFieldIds.includes(_field.id)
      );
      toggleControlledField &&
        toggleControlledFields.push(toggleControlledField);
    }
  });
  return toggleControlledFields;
};

export const getRlTableColumns = (
  fields: IFieldInstance[],
  allLookUpOptions: any
) => {
  const columns: any = [];
  let childFields: IFieldInstance[] = [];
  const _allLookUpOptions = allLookUpOptions
    ? Object.values(allLookUpOptions).flat()
    : [];
  fields.forEach((field) => {
    if (childFields.find((_field) => _field.id === field.id)) {
      return;
    }
    if (field.tableLabel === "-") {
      return;
    }
    if (!field.parentFieldId) {
      const accessor = (row: { [x: string]: any }) => {
        const toggleBoxDetails = toggleBoxLogic(field, fields, row);
        return getIndependentFieldAccessor(
          field,
          fields,
          toggleBoxDetails,
          _allLookUpOptions,
          row
        );
      };

      columns.push({
        Header: field.tableLabel || field.fieldLabel,
        accessor,
      });
    } else {
      childFields = findChildFields(fields, field.parentFieldId);
      const toggleControlledFields: IFieldInstance[] = extendedChildFields(
        childFields,
        fields
      );
      childFields.push(...toggleControlledFields);
      const parentField = fields.find(
        (_field) => _field.id === field.parentFieldId
      );
      const parentFields = fields.filter(
        (field) => field.fieldName === parentField?.fieldName
      );
      if (parentFields.some((field) => field.parentFieldId !== "")) {
        const nthOrderParentId = parentFields[0].parentFieldId;
        const directParentFields = fields.filter(
          (field) => field.parentFieldId === nthOrderParentId
        );
        const parentIds = directParentFields.map((field) => field.id);
        childFields = directParentFields
          .map((field) => findChildFields(fields, field.id))
          .flat();
        const toggleControlledFields: IFieldInstance[] = extendedChildFields(
          childFields,
          fields
        );
        childFields.push(...toggleControlledFields);
        const nthOrderDepColumn = getDependentFieldColumns(
          _allLookUpOptions,
          fields,
          field,
          parentIds
        );
        columns.push(nthOrderDepColumn);
        return;
      }

      const firstOrderDepColumn = getDependentFieldColumns(
        _allLookUpOptions,
        fields,
        field
      );
      columns.push(firstOrderDepColumn);
    }
  });
  return columns;
};
